<script setup>
import { computed, ref, onMounted, onUnmounted,watch } from "vue"
import { store } from '@/store/index.js'
import { useMsal } from '@/composition-api/useMsal.js'
import { getAuthProvider } from '@/oidc-config.js'
import { azureADAuthentication } from '@/components/ui/AzureADAuthentication.js'

const { instance } = useMsal()

const oidcIsAuthenticated = computed(() => {
  return (
    !!store.state.oidc?.access_token &&
    store.state.oidc?.expires_at > Date.now()
  )
})
const adIsAuthenticated = () => store.state.bearerToken !== null

const getProvider = () => getAuthProvider(store.state.oidc?.user)
const getUsername = () => {
  const username = store.state.oidc?.user.name

  if (!username) return null

  return String(username).trim().toUpperCase()
}
// Static role mapping
const roleDescriptions = {
  RnDemployee: "R&D Employee",
  RnDconsultant: "R&D Consultant",
  RnDAdvancedAnalysisPlatform: "Product Analytics Internal",
  ODP_unlimited: "Basic for Scania R&D",
  ODP_limited: "Basic for Scania",
  Product_Analytics_Download: "Download",
  Freud_standard: "Freud Standard",
  YQI_Users: "Product Quality user"
}

// Priority list for sorting roles
const priorityItems = [
  "RnDemployee",
  "RnDconsultant",
  "RnDAdvancedAnalysisPlatform",
  "ODP_unlimited",
  "ODP_limited",
  "YQI_Users"
]


// Static role mapping
const roleDescriptionsTraton = {
  RnDemployee: "R&D Employee",
  RnDconsultant: "R&D Consultant",
  RnDAdvancedAnalysisPlatform: "SAS Viya admin",
  ODP_unlimited: "Analytics RnD user",
  ODP_limited: "Analytics user",
  Product_Analytics_Download: "Download access",
  FREUD_standard: "Test Product Diagnostics user",
  YQI_Users:"Product Quality user",
  "AP3610001-CLAPUsers":"Logger Data user",
  "AP3610001-CLAPAdmins":"Logger data admin"
}

// Priority list for sorting roles
const priorityItemsTraton = [
  "RnDemployee",
  "RnDconsultant",
  "RnDAdvancedAnalysisPlatform",
  "ODP_unlimited",
  "ODP_limited",
  "YQI_Users",
  "FREUD_standard",
  "Product_Analytics_Download",
  "AP3610001-CLAPUsers",
  "AP3610001-CLAPAdmins"
]

const oidcRoles = computed(() => {
  const user = store.state.oidc?.user || {}

  const roles =
    user.roles ||
    user['cognito:groups'] ||
    []

  return Array.isArray(roles) ? roles : [roles]
})
const userRoleDescription = ref([])

const updateUserRoleDescription = () => {
  const provider = getProvider()
  let roles = []
  let descriptions = roleDescriptions
  let priorities = priorityItems

  if (provider === 'NAVISTAR' || provider === 'MAN') {
    roles = oidcRoles.value
    descriptions = roleDescriptionsTraton
    priorities = priorityItemsTraton
  } else if (provider === 'SCANIA') {
    try {
      roles = JSON.parse(localStorage.getItem('yourAdRoles') || '[]')
    } catch {
      roles = []
    }
  }

  userRoleDescription.value = sortByPriority(roles, priorities)
    .map(role => descriptions[role] || role)
}
// Function to sort the array based on priority
const sortByPriority = (array, priorityList) => {
  if (!Array.isArray(array)) return [] // Ensure array is valid

  return array.sort((x, y) => {
    const xPriority = priorityList?.includes(x) ? priorityList.indexOf(x) : priorityList.length
    const yPriority = priorityList?.includes(y) ? priorityList.indexOf(y) : priorityList.length
    return xPriority - yPriority // Sort priority items first
  })
}

const authenticate = async () => {
  console.log('Authenticating...')

  try {
    if (!oidcIsAuthenticated.value) {
      console.log('OIDC not authenticated, starting OIDC auth...')
      await store.dispatch('oidc/authenticateOidc', true)
      updateUserRoleDescription()
    }
  
    
  } catch (error) {
    console.error('Authentication error:', error)
    throw error
  }
  
}
// Logout logic
const handleLogout = async () => {
  console.log("Logging out...")
  isOpen.value = false // Close dropdown
  try {
    
    const provider = getProvider()
    if (provider === 'SCANIA') {
      await store.dispatch('oidc/signOutOidc', true)
       return instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + "/callback"
      })
    }
    await store.dispatch('oidc/signOutOidc', true)

    localStorage.setItem('adTokenUpdate', null)
    localStorage.setItem('sasTokenUpdate', null)
    console.log('Signed out')
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

// Reactive variables
const isOpen = ref(false)
const isMenuOpen = ref(false)

const closeMenu = () => {
  isMenuOpen.value = false
}

// Close dropdown when clicking outside
const dropdown = ref(null)
const closeDropdown = (event) => {
  if (dropdown.value && !dropdown.value.contains(event.target)) {
    isOpen.value = false
  }
}

// Add event listener on mount
onMounted(() => {
  document.addEventListener("click", closeDropdown)
})

// Remove event listener when component unmounts
onUnmounted(() => {
  document.removeEventListener("click", closeDropdown)
})




watch(
  [oidcRoles, () => getProvider()],
  ([isAuthenticated]) => {
    if (isAuthenticated) {
      updateUserRoleDescription()
    }
  },
  { immediate: true }
)
</script>

<template>
  <div class="navigation">
    <nav :class="{ 'active': isMenuOpen }">
      <!-- Navigation Links -->
      <ul class="menu-list">
        <!-- Home with Dropdown -->
        <li class="menu-item">
          <button v-if="!adIsAuthenticated() && !oidcIsAuthenticated" class="btn btn btn-secondary" @click="authenticate()">
            Login &nbsp;
          </button>
          <button v-if="adIsAuthenticated() || oidcIsAuthenticated" class="btn btn btn-secondary" @click="closeMenu()">
            <font-awesome-icon icon="fa-user" /> &nbsp;
            {{ getUsername() || 'USER' }}
          </button>
          <ul v-if="oidcIsAuthenticated || adIsAuthenticated()" class="dropdown">
            <li class="dropdown-header">Your Roles:</li>
            <li class="dropdown-item">Provider: {{ getProvider() || 'UNKNOWN' }}</li>
            <li v-for="(item, index) in userRoleDescription" :key="index" class="dropdown-item">
              {{ item }}
            </li>
            <hr class="dropdown-divider" />
            <li class="dropdown-footer">
              <button class="btn btn-sm btn-primary" @click="handleLogout">Logout</button>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  </div>
</template>


<style scoped>
/* Styling for navigation */
.navigation {
  display: flex;
  align-items: center;
  position: relative;
  padding: 12px;
  z-index: 999; /* Ensure the navigation stays on top of other content */
}

nav ul.menu-list {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  justify-content: flex-start;
}

.menu-list .menu-item {
  position: relative; /* For dropdown positioning */
  width: fit-content;
}

.menu-list .menu-item .dropdown {
  display: none;
  position: absolute;
  top: 100%;
  background-color: white;
  border: 1px solid #ccc;
  list-style: none;
  padding: 5px;
  border-radius: 2%;
  width: fit-content;
  margin-left: -20px;
}

.menu-list .menu-item:hover .dropdown {
  display: block;
}

.menu-list .dropdown li {
  padding: 5px;
}

.menu-list .dropdown li a {
  color: #333;
  text-decoration: none;
}

.menu-list .parent {
  color: #333;
  text-decoration: none;
  margin-right: 25px;
}

hr {
  width: 100%;
  border: 1px solid #ccc;
  margin: 10px 0;
}

.menu-links {
  display: flex;
  align-items: center;
  margin-left: auto; /* Push content to the right */
  gap: 15px;
}

.vertical-line {
  border-left: 1px solid #ccc;
  height: 30px;
  margin-right: 10px;
}

/* Optional: Mobile Responsiveness */
@media (max-width: 768px) {
  nav ul.menu-list {
    flex-direction: column;
  }
}

/* Dropdown Container */
.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 200px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  padding: 10px 0;
  list-style: none;
  z-index: 1000;
  border: 1px solid #ddd;
  animation: fadeIn 0.2s ease-in-out;
}

/* Dropdown Header */
.dropdown-header {
  font-weight: bold;
  padding: 10px 15px;
  border-bottom: 1px solid #ddd;
}

.dropdown-item:hover {
  background: #f0f0f0;
}

/* Dropdown Divider */
.dropdown-divider {
  margin: 5px 0;
  border: none;
  border-top: 1px solid #ddd;
}

/* Dropdown Footer */
.dropdown-footer {
  padding: 10px;
  text-align: center;
}

/* Fade-in Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
